import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {CustomPropertyValuesTable} from '@github-ui/custom-properties-editing'
import {
  propertyFieldsToChangedPropertiesMap,
  useEditCustomProperties,
} from '@github-ui/custom-properties-editing/use-edit-custom-properties'
import {customPropertyValueValidator} from '@github-ui/custom-properties-editing/validate'
import type {
  PropertiesLimits,
  PropertyDefinitionBase,
  PropertySourceDetails,
  PropertyTarget,
  PropertyValuesRecord,
} from '@github-ui/custom-properties-types'
import {useMutation} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {SearchIcon} from '@primer/octicons-react'
import {Button, TextInput, useConfirm} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useEffect, useMemo, useState} from 'react'

import styles from './PropertiesEditingPage.module.css'
import {ServerErrorFormBanner} from './ServerErrorFormBanner'

const debounceAnnouncement = debounce(announce, 300)

const DEFAULT_ACTION_LABELS = {save: 'Save', cancel: 'Cancel'}

export function PropertiesEditingPage({
  editingTargets,
  onClose,
  editableProperties,
  propertiesLimits,
  definitions,
  onSuccess,
  renderPageHeader,
  actionLabels = DEFAULT_ACTION_LABELS,
  setValuesPath,
  // The source level where said property can be managed, i.e. have values set
  managedBySource,
}: {
  editableProperties: string[]
  propertiesLimits?: PropertiesLimits
  editingTargets: PropertyTarget[]
  onClose?: () => void
  definitions: PropertyDefinitionBase[]
  onSuccess: () => void
  renderPageHeader: ({canClose}: {canClose: () => Promise<boolean>}) => React.ReactElement
  actionLabels?: {save?: string; cancel?: string}
  setValuesPath: string
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
}) {
  const propertyValueValidator = useMemo(
    () => customPropertyValueValidator(definitions, propertiesLimits),
    [definitions, propertiesLimits],
  )

  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editableProperties.includes(propertyName)),
    [definitions, editableProperties],
  )

  const {propertyValuesMap, setPropertyValue, revertPropertyValue, discardChanges, commitChanges} =
    useEditCustomProperties({
      propertyValues: editingTargets.map(t => t.properties || {}),
      editableDefinitions,
      propertyValueValidator,
    })

  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const confirm = useConfirm()

  const hasUnsavedChanges = useMemo(() => Object.values(propertyValuesMap).some(p => p.changed), [propertyValuesMap])
  useNavigateAwayDiscardConfirmation(hasUnsavedChanges)

  const canClose = async () => {
    if (hasUnsavedChanges) {
      return confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to discard them?',
        cancelButtonContent: 'Keep editing',
        confirmButtonContent: 'Discard',
        confirmButtonType: 'danger',
      })
    } else {
      return true
    }
  }
  const discardWithConfirmation = async () => {
    if (hasUnsavedChanges) {
      if (await canClose()) {
        discardChanges()
        onClose?.()
      }
    } else {
      onClose?.()
    }
  }

  const saveMutation = useMutation({
    onMutate: async () => {
      if (!(await sudo())) throw new Error('Unauthorized')
    },
    mutationFn: async ({
      targetIds,
      changedProperties,
    }: {
      targetIds: number[]
      changedProperties: PropertyValuesRecord
    }) => {
      const response = await reactFetchJSON(setValuesPath, {
        method: 'POST',
        body: {
          targetIds,
          properties: changedProperties,
        },
      })

      if (response.ok) return

      let error: string | undefined
      try {
        const data = (await response.json()) as {error?: string}
        error = data.error
      } catch {
        // ignore JSON parse errors
      }
      throw new Error(error ?? 'Failed to update properties')
    },
    onSuccess: () => {
      commitChanges()
      onSuccess()
    },
    onError: error => {
      setErrorMessage(error.message)
    },
  })

  const onSubmit = async () => {
    // Clear any previous error here (instead of in onMutate) so we also handle
    // the case where the mutation is never called (e.g. no properties changed).
    // Doing this inside the mutation would treat a no-op as success and trigger
    // onSuccess, which is not desired.
    setErrorMessage('')
    if (saveMutation.isPending) {
      return
    }

    if (Object.values(propertyValuesMap).some(p => !!p.error)) {
      return
    }

    const changedProperties = propertyFieldsToChangedPropertiesMap(definitions, propertyValuesMap)
    if (Object.keys(changedProperties).length) {
      const targetIds = editingTargets.map(target => target.id)
      saveMutation.mutate({targetIds, changedProperties})
    } else {
      onClose?.()
    }
  }

  const filteredDefinitions = definitions.filter(({propertyName}) =>
    propertyName.toLowerCase().includes(filterText.toLowerCase()),
  )

  useEffect(() => {
    const resultsFound = filteredDefinitions.length
    if (resultsFound === 1) {
      debounceAnnouncement('1 result found')
    } else {
      debounceAnnouncement(`${resultsFound} results found`)
    }
  }, [filteredDefinitions])

  if (!editingTargets.length) {
    return null
  }

  return (
    <>
      {errorMessage && (
        <div className={styles.errorContainer}>
          <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
        </div>
      )}
      {renderPageHeader({canClose})}
      <form data-testid="properties-editing-page" className={styles.propertiesEditingForm}>
        <TextInput
          block
          leadingVisual={SearchIcon}
          placeholder="Filter properties"
          aria-label="Filter properties"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />

        {filteredDefinitions.length === 0 ? (
          <Blankslate border>
            <Blankslate.Heading>No properties that match</Blankslate.Heading>
          </Blankslate>
        ) : (
          <CustomPropertyValuesTable
            definitions={filteredDefinitions}
            editableDefinitions={editableDefinitions}
            propertyValuesMap={propertyValuesMap}
            setPropertyValue={setPropertyValue}
            revertPropertyValue={revertPropertyValue}
            managedBySource={managedBySource}
          />
        )}

        <div className={styles.formActionsContainer}>
          {actionLabels.save && (
            <Button type="button" onClick={onSubmit} variant="primary">
              {actionLabels.save}
            </Button>
          )}

          {actionLabels.cancel && (
            <Button type="button" onClick={discardWithConfirmation} variant="default">
              {actionLabels.cancel}
            </Button>
          )}
        </div>
      </form>
    </>
  )
}

function useNavigateAwayDiscardConfirmation(hasUnsavedChanges: boolean) {
  useEffect(() => {
    document.body.setAttribute('data-turbo', 'false')
    window.onbeforeunload = hasUnsavedChanges
      ? e => {
          e.returnValue = 'You have unsaved changes. Are you sure you want to leave this page?'
        }
      : null

    return () => {
      document.body.removeAttribute('data-turbo')
      window.onbeforeunload = null
    }
  }, [hasUnsavedChanges])
}

try{ PropertiesEditingPage.displayName ||= 'PropertiesEditingPage' } catch {}